import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useUserRole, type AppRole } from "@/hooks/useUserRole";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Require one of these app_role values (from user_roles table). Pass array to allow any of them. */
  requiredRole?: AppRole | AppRole[];
  /** Require this profile role (from profiles.role column) */
  requiredProfileRole?: "student" | "partner";
  /** Where to redirect if unauthorized */
  redirectTo?: string;
}

export function ProtectedRoute({
  children,
  requiredRole,
  requiredProfileRole,
  redirectTo = "/",
}: ProtectedRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { roles, isLoading: rolesLoading } = useUserRole();
  const { profile, isLoading: profileLoading } = useProfile();

  const isLoading = authLoading || rolesLoading || (requiredProfileRole ? profileLoading : false);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={redirectTo} replace />;
  }

  if (requiredRole) {
    const allowed = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!allowed.some((r) => roles.includes(r))) {
      return <Navigate to={redirectTo} replace />;
    }
  }

  if (requiredProfileRole && profile?.role !== requiredProfileRole) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
